import { useCallback, useContext, useMemo, useRef } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ChatMapWidget from 'tripPlanner/components/Chat/ChatMapWidget'
import type { ChatMapMarker } from 'tripPlanner/components/ChatMap/types'
import type { MapCameraPosition } from 'tripPlanner/api/types'
import type { MapMarkerComponent } from 'tripPlanner/utils/mapMarkerTransforms'
import { isValidCoordinate } from 'tripPlanner/utils/mapMarkerTransforms'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useMapInteraction } from '../contexts/MapInteractionContext'
import { DrawerSurfaceEntryContext } from '../contexts/DrawerSurfaceEntryContext'
import { useChatActions } from '../contexts/ChatActionsContext'
import { buildDrawerMapMarkers } from './buildDrawerMapMarkers'
import { useTripMapActivation } from '../hooks/useTripMapActivation'

export const DRAWER_DETAIL_MAP_HEIGHT_MOBILE_PX = 260
export const DRAWER_DETAIL_MAP_HEIGHT_DESKTOP_PX = 400
const FOCUSED_ZOOM = 14

export const DrawerDetailMapFrame = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(DRAWER_DETAIL_MAP_HEIGHT_MOBILE_PX)};
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    height: ${rem(DRAWER_DETAIL_MAP_HEIGHT_DESKTOP_PX)};
  }
`

interface Props {
  markers: Array<ChatMapMarker>
  initialCenterPlaceId: string
  itemId?: number
  offerId?: string
  offerType?: 'hotel' | 'experience'
  onEnlarge?: (camera: MapCameraPosition) => void
}

function DrawerDetailMap({ markers, initialCenterPlaceId, itemId, offerId, offerType, onEnlarge }: Props) {
  const { activatePlace, activateOffer } = useTripMapActivation()
  const { itineraryData } = useItineraryContext()
  const { sendMessage } = useChatActions()
  const { itineraryResult, mapStyleMode, onMapStyleModeChange, highlightedMarkerId, onMarkerHover, getDrawerCamera, setDrawerCamera } = useMapInteraction()
  const drawerEntryId = useContext(DrawerSurfaceEntryContext)
  const cameraKey = drawerEntryId ?? (offerId ? `offer-${offerId}` : `place-${initialCenterPlaceId}`)
  const subject = useMemo(() => markers.find(marker => marker.markerKey === initialCenterPlaceId), [markers, initialCenterPlaceId])
  const cameraRef = useRef<MapCameraPosition | undefined>(getDrawerCamera(cameraKey))
  const drawerMapData = useMemo(() => buildDrawerMapMarkers(itineraryResult.markers, {
    marker: subject,
    placeId: initialCenterPlaceId,
    itemId,
    offerId,
    offerType,
  }), [itineraryResult.markers, subject, initialCenterPlaceId, itemId, offerId, offerType])
  const initialCamera = useMemo<MapCameraPosition | undefined>(() => {
    if (!isValidCoordinate(subject) || !drawerMapData.focusedMarker) return undefined
    const { lat, lng } = drawerMapData.focusedMarker
    return { lat, lng, zoom: FOCUSED_ZOOM }
  }, [subject, drawerMapData.focusedMarker])

  const handleMarkerSelect = useCallback((selection: { marker: MapMarkerComponent } | undefined) => {
    const marker = selection?.marker
    if (!marker) return
    if (drawerMapData.focusedItemId !== undefined && marker.itemId === drawerMapData.focusedItemId) return
    if (drawerMapData.focusedItemId === undefined && marker.placeId === initialCenterPlaceId) return
    activatePlace(marker)
  }, [activatePlace, drawerMapData.focusedItemId, initialCenterPlaceId])

  const handleOfferClick = useCallback((data: Parameters<typeof activateOffer>[0]) => {
    if (data.offerId === offerId) return
    activateOffer(data)
  }, [activateOffer, offerId])

  const handleEnlarge = useCallback(() => {
    const target = cameraRef.current ?? initialCamera
    if (target) onEnlarge?.(target)
  }, [initialCamera, onEnlarge])

  const handleViewStateChange = useCallback((viewState: { latitude: number; longitude: number; zoom: number }) => {
    const nextCamera = { lat: viewState.latitude, lng: viewState.longitude, zoom: viewState.zoom }
    cameraRef.current = nextCamera
    setDrawerCamera(cameraKey, nextCamera)
  }, [cameraKey, setDrawerCamera])

  if (!initialCamera) return null

  return (
    <DrawerDetailMapFrame data-testid="drawer-detail-map-frame">
      <ChatMapWidget
        presentation="drawer"
        data={drawerMapData}
        fillContainer
        initialCamera={cameraRef.current ?? initialCamera}
        focusedMarkerId={offerId ?? initialCenterPlaceId}
        focusedItemId={drawerMapData.focusedItemId}
        highlightedMarkerId={highlightedMarkerId}
        onMarkerHover={onMarkerHover}
        onMarkerSelect={handleMarkerSelect}
        onOfferClick={handleOfferClick}
        itemHoverPreview="item-card"
        onViewStateChange={handleViewStateChange}
        onEnlarge={onEnlarge ? handleEnlarge : undefined}
        itineraryData={itineraryData}
        onSendMessage={sendMessage}
        mapStyleMode={mapStyleMode}
        onMapStyleModeChange={onMapStyleModeChange}
      />
    </DrawerDetailMapFrame>
  )
}

export default DrawerDetailMap
